/**
 * Glidejs slider bullet pagination module.
 * Glidejs accepts functions as components ONLY!
 * On default Glide-, Components- and Events-object are passed to a custom glidejs-component
 * @see https://glidejs.com/docs/extending-components/
 * @param {object} Glide
 * @param {object} Components
 * @param {object} Events
 * @constructor
 */

const SliderBulletNav = function SliderBulletNav(Glide, Components, Events) {
  const config = {
    elements: {
      bulletContainer: '[data-slider-bullet-container]',
      bulletTmpl: '#slider-bullet',
      bulletTmplSelector: '[data-slider-bullet="=#index#"]',
      bullet: '[data-slider-bullet]',
      prev: '[data-glide-dir="<"]',
      next: '[data-glide-dir=">"]',
    },
    modifier: {
      arrowDisabled: 'content-slider__pagination-button--disabled',
      bulletActive: 'content-slider__bullet--active',
      oneSlide: 'content-slider__controls--one-slide',
    },
    replacement: '#index#',
  };

  /**
   * Custom glidejs component.
   * Sets navigation bullets dynamically
   * @see https://glidejs.com/docs/extending-components/
   */
  const DynamicBullets = {
    /**
     * global hook for glidejs slider mount.
     * fired when slider is mounted and get the total slide number,
     * set bullet for each slide and bind event for each bullet
     */
    mount() {
      this.totalCount = Components.Sizes.length;
      this.controlsContainer = null;
      [this.controlsContainer] = Components.Controls.items;
      this.prevButton = this.controlsContainer.querySelector(config.elements.prev);
      this.nextButton = this.controlsContainer.querySelector(config.elements.next);
      this.bulletContainer = Glide.selector.querySelector(config.elements.bulletContainer);
      this.bulletTemplate = Glide.selector.querySelector(config.elements.bulletTmpl).innerHTML;

      if (this.totalCount > 1) {
        this.setBullets();
        this.updateBulletButton(Glide.index);
        this.bindBulletEvents();
        this.updateArrowButtons();
      } else {
        this.controlsContainer.classList.add(config.modifier.oneSlide);
        Glide.disable();
      }
    },

    /**
     * Delegates click-event from bullet-container
     */
    bindBulletEvents() {
      this.bulletContainer.addEventListener('click', (event) => { this.navigateTo(event); });
    },

    /**
     * Gets data-attribute [data-slider-bullet] from event.target if exists
     * and navigates slider to the value of that data-attribute
     * @param {Event} event
     */
    navigateTo(event) {
      const clickTarget = event.target;
      const { sliderBullet: target } = clickTarget.dataset;
      if (target) {
        Glide.go(target);
      }
    },

    /**
     * Get template-snippet for bullets,
     * replaces placeholder "#index#" with a slide number for each slide
     * and puts all bullets into the bullet-container.
     * If a perView Glidejs-option exists: reduce amount bullets to the number (1 - perView)
     * which is the number where all items are visible on the slider end
     */
    setBullets() {
      const bulletArr = [];
      const pages = this.getMaxPageNumber();

      for (let i = 0; i < pages; i += 1) {
        bulletArr.push(this.bulletTemplate.replace(config.replacement, `${i}`));
      }
      this.bulletContainer.innerHTML = bulletArr.join('');
      this.allBullets = this.bulletContainer.querySelectorAll(config.elements.bullet);
    },

    /**
     * Returns current max page number
     * @return {number} - max page Number
     */
    getMaxPageNumber() {
      // eslint-disable-next-line no-underscore-dangle
      const { perView } = Glide._o;
      if (perView && this.totalCount > perView) {
        return this.totalCount + (1 - perView);
      }
      return 1;
    },

    /**
     * Resets all bullets and sets new active bullet.
     * When the window is resized, the active bullet might be removed.
     * If so: reset the slider to the first slide
     * @param {number} index - current slide
     */
    updateBulletButton(index) {
      const activeSelector = config.elements.bulletTmplSelector.replace(config.replacement, index);
      const activeBullet = this.bulletContainer.querySelector(activeSelector);
      this.allBullets.forEach((elem) => {
        elem.classList.remove(config.modifier.bulletActive);
      });
      if (!activeBullet) {
        Glide.go('=0');
      } else {
        activeBullet.classList.add(config.modifier.bulletActive);
      }
    },

    /**
     * Custom method for glidejs component
     * updates navigation arrow classes
     */
    updateArrowButtons() {
      const currentPage = Glide.index + 1;

      this.prevButton.classList.remove(config.modifier.arrowDisabled);
      this.nextButton.classList.remove(config.modifier.arrowDisabled);

      switch (currentPage) {
        case 1:
          this.prevButton.classList.add(config.modifier.arrowDisabled);
          break;
        case (this.getMaxPageNumber()):
          this.nextButton.classList.add(config.modifier.arrowDisabled);
          break;
        default:
          break;
      }
    },
  };

  /**
   * fired on glide-event "run.after"
   * "run.after" is fired when slider is moved to next/previous slide
   * @see https://glidejs.com/docs/events/
   */
  Events.on('run.after', () => {
    if (Components.Sizes.length > 1) {
      DynamicBullets.updateArrowButtons();
      DynamicBullets.updateBulletButton(Glide.index);
    }
  });

  /**
   * fired on glide-event "resize"
   * "resize" is fired when viewport is changed
   * and the responsive options of the slider is triggered.
   * @see https://glidejs.com/docs/events/
   */
  Events.on('resize', () => {
    if (Components.Sizes.length > 1) {
      DynamicBullets.setBullets();
      DynamicBullets.updateArrowButtons();
      DynamicBullets.updateBulletButton(Glide.index);
    }
  });

  return DynamicBullets;
};

export default SliderBulletNav;
